// Per-frame simulation. Two clocks are passed in: `dt` is physics-clamped (≤50ms) so the
// simulation never explodes on a hitch; `dtReal` is wall-clock (≤500ms) and drives the
// mash/finale countdowns so a low camera framerate can't stretch those windows
// (critical bug fix — CLAUDE.md §3).
import { G } from './state';
import { MAX_LIVES, MEGA_CAP, MEGA_TIME, POM_TIME, ROUND } from './config';
import { director, grav } from './spawn';
import { flushCombo, gameOver, addScore, mashCenter } from './scoring';
import { addText, shred } from '../fx/effects';
import { sfx } from '../fx/audio';
import { showEnd } from '../ui/screens';

export function update(dt: number, dtReal?: number): void {
  dtReal = dtReal ?? dt;

  if (G.state === 'play' && G.mash) {
    // freeze the world, run only the mash (round timer paused)
    G.mash.t += dtReal;
    G.mash.zoom = Math.min(1, G.mash.zoom + dt * 5); // wall-clock: 5s means 5 real seconds even at low fps
    for (const x of G.texts) {
      x.y -= dt * 40;
      x.life -= dt * 0.9;
    }
    for (const p of G.parts) {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += p.g;
      p.rot += p.vr;
      p.life -= dt;
    }
    G.parts = G.parts.filter((p) => p.life > 0);
    G.texts = G.texts.filter((x) => x.life > 0);
    const now = performance.now();
    G.trail = G.trail.filter((p) => now - p.t < 160);
    if (G.shake > 0) G.shake = Math.max(0, G.shake - dt * 2);
    // No hit cap now — the mash ends only when the 5s timer runs out. (The ZERO DUPLICATES
    // +10 milestone is awarded mid-mash when hits crosses 50, in slice.ts.)
    if (G.mash.t >= POM_TIME) {
      const c = mashCenter();
      addText(G.W / 2, G.H * 0.38, 'ALL DUPES MERGED ×' + G.mash.hits, '#7c3aed', true);
      shred(c.x, c.y, '#8e4ec6', 40);
      shred(c.x, c.y, '#cfd3da', 15);
      G.shake = 0.4;
      sfx.combo();
      G.mash = null;
    }
    return;
  }

  if (G.state === 'play') {
    G.t += dt;
    director(dt);
    if (G.comboTimer > 0) {
      G.comboTimer -= dt;
      if (G.comboTimer <= 0) flushCombo();
    }
    if (G.t >= ROUND) {
      flushCombo();
      G.state = 'finale';
      G.finaleT = 0;
      G.objs = [];
      addText(G.W / 2, G.H * 0.25, 'FINAL BOSS: THE NEVER-ENDING THREAD', '#b45309', true);
    }
  }

  if (G.state === 'finale') {
    G.finaleT += dtReal;
    if (G.finaleT >= MEGA_TIME || G.megaHits >= MEGA_CAP) {
      G.shake = 0.5;
      sfx.combo();
      setTimeout(showEnd, 500);
      G.state = 'dead';
    }
  }

  const g = grav() * dt * 60;
  for (const o of G.objs) {
    if (o.dead) continue;
    if (o.kind === 'chute') {
      o.sway = (o.sway ?? 0) + dt * 2;
      o.x += Math.sin(o.sway) * 0.6;
      o.y += o.vy * dt * 60 * (G.H / 800);
      if (o.y > G.H + 100) o.dead = true;
      continue;
    }
    o.x += o.vx * dt * 60;
    o.y += o.vy * dt * 60;
    o.vy += g;
    o.rot = (o.rot ?? 0) + (o.vr ?? 0);
    if (o.happy) {
      if (o.y < -80) o.dead = true;
      continue;
    }
    if (o.y > G.H + o.r + 40 && o.vy > 0) {
      o.dead = true;
      if (o.kind === 'pom') {
        addText(o.x, G.H - 80, 'the storm passes…', '#6b7280');
      }
      if (o.kind === 'ticket' && G.state === 'play') {
        G.missed++;
        G.breaches++;
        sfx.miss();
        addText(o.x, G.H - 60, 'SLA BREACH', '#dc2626', true);
        G.shake = Math.max(G.shake, 0.2);
        if (G.missed >= MAX_LIVES) {
          gameOver('sla');
        }
      }
    }
  }
  for (const h of G.halves) {
    h.x += h.vx;
    h.y += h.vy;
    h.vy += g;
    h.rot += h.vr;
    h.life -= dt;
  }
  for (const p of G.parts) {
    p.x += p.vx;
    p.y += p.vy;
    p.vy += p.g;
    p.rot += p.vr;
    p.life -= dt;
  }
  for (const x of G.texts) {
    x.y -= dt * 40;
    x.life -= dt * 0.9;
  }
  G.objs = G.objs.filter((o) => !o.dead);
  G.halves = G.halves.filter((h) => h.life > 0);
  G.parts = G.parts.filter((p) => p.life > 0);
  G.texts = G.texts.filter((x) => x.life > 0);
  const now = performance.now();
  G.trail = G.trail.filter((p) => now - p.t < 160);
  if (G.shake > 0) G.shake = Math.max(0, G.shake - dt * 2);
}
